/**
 * Loading Component with Timeout Message
 * Shows "Taking longer than usual..." after 5 seconds
 */
import { useState, useEffect } from 'react';
import { AlertTriangle, Icon, Loader2, RefreshCw } from 'lucide-react';

export const LoadingSpinner = ({ size = 24, text = 'Loading...' }) => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    justifyContent: 'center',
    gap: '12px',
    padding: '20px'
  }}>
    <Loader2 
      size={size} 
      style={{ 
        color: '#FF4081', 
        animation: 'spin 1s linear infinite' 
      }} 
    />
    <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{text}</span>
    <style>{`
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

export const LoadingWithTimeout = ({ 
  isLoading, 
  timeout = 5000, 
  children,
  loadingText = 'Memuatkan...',
  slowText = 'Mengambil masa lebih lama dari biasa...',
  errorText = 'Server mungkin sibuk. Cuba refresh.',
  onRetry
}) => {
  const [isSlow, setIsSlow] = useState(false);
  const [isVeryLate, setIsVeryLate] = useState(false);

  useEffect(() => {
    let slowTimer;
    let veryLateTimer;

    if (isLoading) {
      setIsSlow(false);
      setIsVeryLate(false);
      
      slowTimer = setTimeout(() => {
        setIsSlow(true);
      }, timeout);

      veryLateTimer = setTimeout(() => {
        setIsVeryLate(true);
      }, timeout * 2);
    }

    return () => {
      clearTimeout(slowTimer);
      clearTimeout(veryLateTimer);
    };
  }, [isLoading, timeout]);

  if (!isLoading) {
    return children;
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      gap: '16px',
      padding: '40px 20px',
      minHeight: '200px'
    }}>
      <Loader2 
        size={32} 
        style={{ 
          color: isVeryLate ? '#F59E0B' : '#FF4081', 
          animation: 'spin 1s linear infinite' 
        }} 
      />
      
      <div style={{ textAlign: 'center' }}>
        <p style={{ 
          fontSize: '14px', 
          color: 'var(--text-primary)',
          margin: '0 0 4px 0'
        }}>
          {isVeryLate ? '⏳ ' + errorText : isSlow ? '⏳ ' + slowText : loadingText}
        </p>
        
        {isSlow && !isVeryLate && (
          <p style={{ 
            fontSize: '12px', 
            color: 'var(--text-muted)',
            margin: 0
          }}>
            Sila tunggu sebentar...
          </p>
        )}
      </div>

      {isVeryLate && onRetry && (
        <button
          onClick={onRetry}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            background: '#FF4081',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600'
          }}
        >
          <RefreshCw size={16} />
          Cuba Lagi
        </button>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export const ErrorState = ({ 
  title = 'Ralat Berlaku',
  message = 'Sesuatu tidak kena. Sila cuba lagi.',
  onRetry,
  icon: Icon = AlertTriangle
}) => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    justifyContent: 'center',
    gap: '16px',
    padding: '40px 20px',
    textAlign: 'center'
  }}>
    <div style={{
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      background: 'rgba(239, 68, 68, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Icon size={28} style={{ color: '#EF4444' }} />
    </div>
    
    <div>
      <h3 style={{ 
        margin: '0 0 8px 0', 
        fontSize: '16px', 
        color: 'var(--text-primary)' 
      }}>
        {title}
      </h3>
      <p style={{ 
        margin: 0, 
        fontSize: '13px', 
        color: 'var(--text-muted)',
        maxWidth: '300px'
      }}>
        {message}
      </p>
    </div>

    {onRetry && (
      <button
        onClick={onRetry}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 20px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '8px',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          fontSize: '13px'
        }}
      >
        <RefreshCw size={16} />
        Cuba Lagi
      </button>
    )}
  </div>
);

export const EmptyState = ({ 
  title = 'Tiada Data',
  message = 'Tiada data untuk dipaparkan.',
  icon: Icon = AlertTriangle,
  action
}) => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    justifyContent: 'center',
    gap: '16px',
    padding: '40px 20px',
    textAlign: 'center'
  }}>
    <div style={{
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      background: 'rgba(255, 64, 129, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Icon size={28} style={{ color: '#FF4081', opacity: 0.7 }} />
    </div>
    
    <div>
      <h3 style={{ 
        margin: '0 0 8px 0', 
        fontSize: '16px', 
        color: 'var(--text-primary)' 
      }}>
        {title}
      </h3>
      <p style={{ 
        margin: 0, 
        fontSize: '13px', 
        color: 'var(--text-muted)',
        maxWidth: '300px'
      }}>
        {message}
      </p>
    </div>

    {action}
  </div>
);

export default LoadingWithTimeout;
